import { Link } from '@tanstack/react-router';
import { Truck } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { useBatchConnectionId } from '~/features/catalog-operations/hooks/use-batch-connection-id';
import type { BatchStatus } from '~/features/catalog-operations/types/catalog-operations.types';
import {
  isGmcConnectorCode,
  isWorkspaceConnectorCode,
} from '~/features/deliveries/utils/workspace-connector';
import { useAuthStore } from '~/store/auth.store';
import { canAccessNavPath } from '~/utils/access';

interface ProductDeliveryHistoryLinkProps {
  /** Batch header connector code (`GMC`/`META`); absent on the needs-attention route. */
  batchConnectorCode: string | null | undefined;
  /** Product's curation feed source (`GOOGLE_MERCHANT`/`META_COMMERCE`) — fallback connector. */
  feedSource: string | null | undefined;
  /** GMC resource name (`accounts/…/products/…`) — the id GMC timelines match on. */
  externalId: string | null | undefined;
  /** Retailer SKU — the id Meta timelines match on. */
  sku: string | null | undefined;
  /** Version the live feed row holds; undefined until curation ships versioning. */
  liveVersion: number | undefined;
  /** Batch status from the header; undefined when there is no batch (needs-attention). */
  batchStatus: BatchStatus | undefined;
  /** This page's route ids — the workspace's back link returns here. Empty `batchId` = needs-attention. */
  batchId: string;
  productId: string;
}

/**
 * Deep link from a product to its delivery jobs in the connection workspace's
 * Products tab. Only for users who can open the `/deliveries` tree (tenant admins and
 * super admins — `canAccessNavPath`, the same predicate as its `requireTenantAdmin` guard);
 * passes the id the workspace's default "Match on" expects so the lookup runs on arrival.
 * Shown only when there is delivery history worth opening: the product has more than one
 * version, or — with a single version — its batch has been delivered.
 */
export function ProductDeliveryHistoryLink({
  batchConnectorCode,
  feedSource,
  externalId,
  sku,
  liveVersion,
  batchStatus,
  batchId,
  productId,
}: ProductDeliveryHistoryLinkProps) {
  const { t } = useTranslation('catalog-operations');
  const canOpenDeliveries = useAuthStore((s) => canAccessNavPath('/deliveries', s.user));
  // Needs-attention products have no batch, so fall back to the product's own feed source.
  const connectorCode = batchConnectorCode ?? feedSource ?? null;
  const { connectionId } = useBatchConnectionId(canOpenDeliveries ? connectorCode : null);

  // Versions are numbered from 1, so a live version above 1 means the history holds more
  // than one. No version info counts as a single version, which needs a delivered batch.
  const hasMultipleVersions = liveVersion !== undefined && liveVersion > 1;
  if (!hasMultipleVersions && batchStatus !== 'delivered') return null;
  if (!canOpenDeliveries || !connectionId || !isWorkspaceConnectorCode(connectorCode)) return null;
  // GMC workspaces default to matching on the connector (external) id, Meta on the retailer SKU.
  const lookupId = isGmcConnectorCode(connectorCode) ? externalId : sku;
  if (!lookupId) return null;

  const label = t('detailHeader.deliveryHistoryTooltip');
  // Icon-only, sized and styled to sit beside the header's refresh button.
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button asChild variant="ghost" size="icon" className="shrink-0 cursor-pointer">
          <Link
            to="/deliveries/connections/$connectionId"
            params={{ connectionId }}
            search={{
              tab: 'products',
              sku: lookupId,
              source: 'product-detail',
              sourceProductId: productId,
              ...(batchId ? { sourceBatchId: batchId } : {}),
            }}
            aria-label={label}
            data-testid="product-detail-delivery-history-link"
          >
            <Truck className="h-4 w-4" aria-hidden="true" />
          </Link>
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom">{label}</TooltipContent>
    </Tooltip>
  );
}
